import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, RotateCcw, RotateCw, FlipHorizontal2, Crop, Undo2, Redo2,
  Eye, Save, Cpu, Sparkles, History, Check, X, Download,
} from "lucide-react";
import { api, mediaUrl, errMsg, triggerDownload } from "@/lib/galleryApi";
import { createEngine, fetchBitmap, autoAdjust, frameSize, FULL_CROP, TONAL_KEYS } from "@/lib/photoEngine";
import { useBatchExport } from "@/hooks/useBatchExport";
import { AdjustmentPanel } from "@/components/studio/AdjustmentPanel";
import { PresetPanel } from "@/components/studio/PresetPanel";
import { Filmstrip } from "@/components/studio/Filmstrip";
import { BatchPanel } from "@/components/studio/BatchPanel";
import { CropOverlay, ASPECTS, fitCrop } from "@/components/studio/CropOverlay";
import { useStudioPerms } from "@/hooks/useStudioPerms";

const stable = (o) => JSON.stringify(Object.keys(o || {}).sort().reduce((a, k) => (o[k] ? { ...a, [k]: o[k] } : a), {}));
const isStale = (p) => p.has_processed && stable(p.adjustments) !== stable(p.processed_adjustments);
const tool = "shrink-0 h-8 w-8 p-0 bg-transparent hover:bg-white/10 text-[#94A3B8] hover:text-white";

export default function PhotoStudioPage() {
  const perms = useStudioPerms();
  if (!perms) return <div className="h-[100dvh] bg-[#05080F]" />;
  if (!perms.studio) return <Navigate to="/app/galeries" replace />;
  return <Studio />;
}

function Studio() {
  const { galleryId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [adj, setAdj] = useState({});
  const [hist, setHist] = useState({ past: [], future: [] });
  const [before, setBefore] = useState(false);
  const [crop, setCrop] = useState(null); // {draft, aspect} while cropping
  const [selected, setSelected] = useState(new Set());
  const [presets, setPresets] = useState([]);
  const [includeGeo, setIncludeGeo] = useState(false);
  const [imgReady, setImgReady] = useState(false);
  const [dims, setDims] = useState({ w: 0, h: 0, box: { w: 0, h: 0 }, src: [1, 1] });
  const canvasRef = useRef(null);
  const boxRef = useRef(null);
  const engineRef = useRef(null);
  const bitmapRef = useRef(null);
  const committed = useRef({});

  const photo = photos[idx];
  const dirty = photo && stable(adj) !== stable(photo.adjustments);
  const batch = useBatchExport((p) => setPhotos((prev) => prev.map((x) => (x.id === p.id ? p : x))));

  useEffect(() => {
    Promise.all([api.get(`/galleries/${galleryId}`), api.get(`/galleries/${galleryId}/photos`), api.get("/photo-studio/presets")])
      .then(([g, ps, pr]) => {
        setGallery(g);
        const list = ps.filter((p) => !p.is_rejected && !p.in_trash && !p.archived);
        setPhotos(list);
        setPresets(pr);
        const ids = (params.get("ids") || "").split(",").filter(Boolean);
        if (ids.length) {
          setSelected(new Set(ids));
          setIdx(Math.max(0, list.findIndex((p) => p.id === ids[0])));
        }
      })
      .catch((e) => toast.error(errMsg(e)));
    try { engineRef.current = createEngine(canvasRef.current); } catch (e) { toast.error(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId]);

  // Load preview image of current photo
  useEffect(() => {
    if (!photo || !engineRef.current) return;
    let alive = true;
    setImgReady(false);
    const a = photo.adjustments || {};
    setAdj(a);
    committed.current = a;
    setHist({ past: [], future: [] });
    setCrop(null);
    fetchBitmap(mediaUrl(photo.preview_url)).then((bmp) => {
      if (!alive) return bmp.close?.();
      bitmapRef.current?.close?.();
      bitmapRef.current = bmp;
      engineRef.current.setImage(bmp);
      setImgReady(true);
    }).catch((e) => toast.error(errMsg(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo?.id]);

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setDims((d) => ({ ...d, box: { w: e.contentRect.width, h: e.contentRect.height } })));
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, []);

  // Live render (GPU)
  useEffect(() => {
    if (!imgReady) return;
    const id = requestAnimationFrame(() => {
      const view = before ? {} : adj;
      const r = engineRef.current.render(view, { maxSize: 2048, ignoreCrop: !!crop });
      const b = bitmapRef.current;
      setDims((d) => ({ ...d, w: r.width, h: r.height, src: frameSize(b.width, b.height, view) }));
    });
    return () => cancelAnimationFrame(id);
  }, [adj, before, crop, imgReady]);

  const commit = useCallback((next) => {
    const value = next || adj;
    if (stable(value) === stable(committed.current) && !next) return;
    const prevCommitted = committed.current;
    setHist((h) => ({ past: [...h.past.slice(-49), prevCommitted], future: [] }));
    committed.current = value;
    if (next) setAdj(next);
  }, [adj]);

  const undo = () => {
    if (!hist.past.length) return;
    const prev = hist.past[hist.past.length - 1];
    const cur = committed.current;
    committed.current = prev;
    setAdj(prev);
    setHist({ past: hist.past.slice(0, -1), future: [cur, ...hist.future] });
  };
  const redo = () => {
    if (!hist.future.length) return;
    const [nxt, ...rest] = hist.future;
    const cur = committed.current;
    committed.current = nxt;
    setAdj(nxt);
    setHist({ past: [...hist.past, cur], future: rest });
  };

  const saveAdj = useCallback(async (silent) => {
    if (!photo || !dirty) return photo;
    try {
      await api.patch(`/galleries/photos/${photo.id}`, { adjustments: adj });
      const updated = { ...photo, adjustments: adj };
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? updated : p)));
      if (!silent) toast.success("Reglages enregistres (non destructif)");
      return updated;
    } catch (e) { toast.error(errMsg(e)); return photo; }
  }, [photo, adj, dirty]);

  const go = useCallback(async (i) => {
    if (i < 0 || i >= photos.length || i === idx) return;
    await saveAdj(true);
    setIdx(i);
  }, [photos.length, idx, saveAdj]);

  const exportCurrent = async () => {
    const p = await saveAdj(true);
    const errors = await batch.run([{ ...p, adjustments: adj }]);
    if (!errors.length) toast.success("JPEG haute qualite genere — l'original est conserve");
  };

  const revert = async () => {
    try {
      const p = await api.post(`/galleries/photos/${photo.id}/revert`, {});
      setPhotos((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      setAdj({});
      commit({});
      toast.success("Photo revenue a l'original");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyToSelection = async () => {
    await saveAdj(true);
    const ids = [...selected];
    const geo = ["crop", "rotate", "flip_h", "flip_v", "aspect"];
    try {
      await api.post(`/galleries/${galleryId}/photos/copy-adjustments`, { photo_ids: ids, adjustments: adj, include_geometry: includeGeo });
      const tonal = Object.fromEntries(Object.entries(adj).filter(([k]) => !geo.includes(k)));
      setPhotos((prev) => prev.map((p) => {
        if (!ids.includes(p.id)) return p;
        const keep = Object.fromEntries(Object.entries(includeGeo ? adj : p.adjustments || {}).filter(([k]) => geo.includes(k)));
        return { ...p, adjustments: { ...tonal, ...keep } };
      }));
      toast.success(`Reglages copies sur ${ids.length} photo(s). Generez les JPEG HD pour finaliser.`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportList = async (list) => {
    await saveAdj(true);
    const fresh = list.map((p) => (p.id === photo.id ? { ...p, adjustments: adj } : p));
    const errors = await batch.run(fresh);
    toast[errors.length ? "warning" : "success"](`${fresh.length - errors.length}/${fresh.length} JPEG HD generes`);
  };

  const editedToExport = useMemo(() => photos.filter((p) => isStale(p) || (!p.has_processed && Object.keys(p.adjustments || {}).length)), [photos]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" && (e.target.type !== "range" || e.key.startsWith("Arrow"))) return;
      if (e.key === "ArrowRight") go(idx + 1);
      if (e.key === "ArrowLeft") go(idx - 1);
      if (e.key === "\\") setBefore((b) => !b);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const setVal = (k, v) => setAdj((a) => ({ ...a, [k]: v }));
  const rotate = (d) => commit({ ...adj, rotate: (((adj.rotate || 0) + d) % 360 + 360) % 360, crop: undefined });
  const startCrop = () => setCrop({ draft: adj.crop || FULL_CROP, aspect: null });
  const pickAspect = (a) => {
    const [fw, fh] = dims.src;
    const ratio = a === "orig" ? fw / fh : a;
    setCrop({ aspect: ratio, draft: fitCrop(ratio, fw, fh) });
  };
  const applyCrop = () => {
    const c = crop.draft;
    commit({ ...adj, crop: c.w > 0.999 && c.h > 0.999 ? undefined : c });
    setCrop(null);
  };
  const applyAuto = () => {
    if (!bitmapRef.current) return;
    const tonal = Object.fromEntries(Object.entries(adj).filter(([k]) => !TONAL_KEYS.includes(k)));
    commit({ ...tonal, ...autoAdjust(bitmapRef.current) });
    toast.success("Correction automatique appliquee — ajustez si besoin");
  };

  const scale = dims.w ? Math.min(dims.box.w / dims.w, dims.box.h / dims.h, 1.5) : 0;

  return (
    <div className="h-[100dvh] bg-[#05080F] text-white flex flex-col overflow-hidden" data-testid="photo-studio-page">
      <header className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 overflow-x-auto border-b border-[#1A2332] bg-[#0B1120] shrink-0">
        <button onClick={async () => { await saveAdj(true); navigate(`/app/galeries/${galleryId}`); }} className="text-[#64748B] hover:text-white" data-testid="studio-back"><ChevronLeft className="w-5 h-5" /></button>
        <div className="min-w-0 mr-auto hidden sm:block">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#F97316] font-semibold">Studio Photo</p>
          <p className="text-xs text-[#94A3B8] truncate">{gallery?.title} {photo && <span className="text-[#4A5568]">· {idx + 1}/{photos.length} · {photo.filename}</span>}</p>
        </div>
        <span className="sm:hidden mr-auto text-[11px] text-[#94A3B8] font-mono">{photo ? `${idx + 1}/${photos.length}` : ""}</span>
        <Button className={tool} onClick={undo} disabled={!hist.past.length} title="Annuler (Ctrl+Z)" data-testid="undo-btn"><Undo2 className="w-4 h-4" /></Button>
        <Button className={tool} onClick={redo} disabled={!hist.future.length} title="Retablir (Ctrl+Maj+Z)" data-testid="redo-btn"><Redo2 className="w-4 h-4" /></Button>
        <Button onClick={() => setBefore((b) => !b)} className={`h-8 text-xs ${before ? "bg-white text-black hover:bg-white/90" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`} title="Raccourci : \" data-testid="before-after-btn"><Eye className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">{before ? "Avant" : "Apres"}</span></Button>
        <Button onClick={revert} disabled={!photo || (!photo.has_processed && !Object.keys(photo.adjustments || {}).length && !dirty)} className="h-8 text-xs bg-white/5 text-[#94A3B8] hover:bg-white/10" title="Revenir a l'original" data-testid="revert-original-btn"><History className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Original</span></Button>
        <Button onClick={() => saveAdj(false)} disabled={!dirty} className="h-8 text-xs bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-adjustments-btn"><Save className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Enregistrer</span></Button>
        <Button onClick={exportCurrent} disabled={!photo || batch.running} className="h-8 text-xs bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="export-btn"><Cpu className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Generer JPEG HD</span></Button>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <aside className="order-2 lg:order-1 lg:w-24 shrink-0 border-t lg:border-t-0 lg:border-r border-[#1A2332] bg-[#080D18] overflow-hidden flex">
          <Filmstrip photos={photos} current={idx} onPick={go} selected={selected} onToggle={(id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })} />
        </aside>

        <main className="order-1 lg:order-2 flex-1 flex flex-col min-h-[45vh] min-w-0">
          <div ref={boxRef} className="flex-1 relative flex items-center justify-center m-3 min-h-0" data-testid="editor-canvas">
            <div className="relative" style={{ width: dims.w * scale, height: dims.h * scale }}>
              <canvas ref={canvasRef} className="w-full h-full block shadow-2xl" data-testid="editor-image" />
              {crop && <CropOverlay crop={crop.draft} aspect={crop.aspect} frameW={dims.src[0]} frameH={dims.src[1]} onChange={(d) => setCrop((c) => ({ ...c, draft: d }))} />}
            </div>
            {!imgReady && photo && <div className="absolute inset-0 flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#F97316]/30 border-t-[#F97316] rounded-full animate-spin" /></div>}
            {!photos.length && gallery && <p className="absolute text-sm text-[#64748B]" data-testid="studio-empty">Aucune photo a traiter dans cette galerie.</p>}
            {before && <span className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/80 text-[11px] px-3 py-1 rounded-full" data-testid="before-label">AVANT — original</span>}
            {idx > 0 && <button onClick={() => go(idx - 1)} className="absolute left-0 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center" data-testid="studio-prev"><ChevronLeft className="w-4 h-4" /></button>}
            {idx < photos.length - 1 && <button onClick={() => go(idx + 1)} className="absolute right-0 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center" data-testid="studio-next"><ChevronRight className="w-4 h-4" /></button>}
          </div>
          <div className="flex items-center gap-1 px-3 py-2 border-t border-[#1A2332] bg-[#0B1120] overflow-x-auto shrink-0" data-testid="geometry-toolbar">
            {crop ? (
              <>
                {ASPECTS.map(([label, a]) => (
                  <button key={label} onClick={() => (a ? pickAspect(a) : setCrop((c) => ({ ...c, aspect: null })))} data-testid={`aspect-${label}`}
                    className="shrink-0 text-[11px] px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[#94A3B8] hover:text-white">{label}</button>
                ))}
                <Button size="sm" onClick={() => setCrop(null)} className="ml-auto h-7 text-xs bg-white/5 text-[#94A3B8]" data-testid="crop-cancel"><X className="w-3.5 h-3.5 mr-1" />Annuler</Button>
                <Button size="sm" onClick={applyCrop} className="h-7 text-xs bg-[#F97316] text-white" data-testid="crop-apply"><Check className="w-3.5 h-3.5 mr-1" />Valider</Button>
              </>
            ) : (
              <>
                <Button className={tool} onClick={() => rotate(-90)} title="Rotation gauche" data-testid="rotate-left-btn"><RotateCcw className="w-4 h-4" /></Button>
                <Button className={tool} onClick={() => rotate(90)} title="Rotation droite" data-testid="rotate-right-btn"><RotateCw className="w-4 h-4" /></Button>
                <Button className={tool} onClick={() => commit({ ...adj, flip_h: !adj.flip_h, crop: undefined })} title="Miroir" data-testid="flip-btn"><FlipHorizontal2 className="w-4 h-4" /></Button>
                <Button size="sm" onClick={startCrop} disabled={!imgReady} className="shrink-0 h-8 text-xs bg-white/5 hover:bg-white/10 text-[#94A3B8]" data-testid="crop-btn"><Crop className="w-3.5 h-3.5 mr-1" />Recadrer</Button>
                <Button size="sm" onClick={applyAuto} disabled={!imgReady} className="shrink-0 h-8 text-xs bg-white/5 hover:bg-white/10 text-[#94A3B8]" data-testid="auto-btn"><Sparkles className="w-3.5 h-3.5 mr-1" />Auto</Button>
                <span className="ml-auto pl-3 text-[10px] text-[#4A5568] font-mono whitespace-nowrap" data-testid="photo-status">
                  {dirty ? "Modifications non enregistrees" : photo && isStale(photo) ? "JPEG HD a regenerer" : photo?.has_processed ? "JPEG HD a jour" : ""}
                </span>
                {photo?.has_processed && <Button className={tool} onClick={() => triggerDownload(mediaUrl(photo.download_url))} title="Telecharger le JPEG HD" data-testid="download-hd-btn"><Download className="w-4 h-4" /></Button>}
              </>
            )}
          </div>
        </main>

        <aside className="order-3 lg:w-72 shrink-0 border-t lg:border-t-0 lg:border-l border-[#1A2332] bg-[#0B1120] overflow-y-auto p-4 space-y-6 max-h-[40vh] lg:max-h-none" data-testid="adjustments-panel">
          <PresetPanel presets={presets} setPresets={setPresets} adj={adj} onApply={(p) => { const geo = Object.fromEntries(Object.entries(adj).filter(([k]) => !TONAL_KEYS.includes(k))); commit({ ...geo, ...p.adjustments }); toast.success(`Preset « ${p.name} » applique`); }} />
          <AdjustmentPanel adj={adj} onChange={setVal} onCommit={() => commit()} />
          <BatchPanel selectedCount={selected.size} includeGeo={includeGeo} setIncludeGeo={setIncludeGeo} batch={batch} editedCount={editedToExport.length}
            onCopy={copyToSelection} onExportSelection={() => exportList(photos.filter((p) => selected.has(p.id)))} onExportEdited={() => exportList(editedToExport)} />
        </aside>
      </div>
    </div>
  );
}
